/**
 * evotech-connect.tsx — Tab "Kết nối dữ liệu" của App Evotech (docs/TICH-HOP-EVOTECH.md):
 * uỷ quyền tài khoản Evotech (OAuth) → chọn cửa hàng → trạng thái đồng bộ. Webhook do eCDP tự đăng ký ở Evotech.
 */
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Database, Globe, Link2, Loader2, Package, PlugZap,
  RefreshCw, ShieldCheck, ShoppingBag, Smartphone, Store, Unplug, Users, Webhook,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiError } from '@/lib/api-client'
import { currentLocale, i18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import {
  useAuthorizeEvotechAi,
  useDisconnectEvotech,
  useDisconnectEvotechOAuth,
  useEvotechConnection,
  useEvotechShops,
  useSelectEvotechShop,
  useEvotechStatus,
  useSaveEvotech,
  useSaveEvotechLinkTemplates,
  useStartEvotechOAuth,
  useSyncEvotech,
  type EvotechConnection,
} from '@/hooks/use-evotech'
import { listenOAuthResult, readOAuthReturn } from '@/lib/evotech-oauth-return'
import { useProducts } from '@/hooks/use-products'
import { useMyPermissions } from '@/hooks/use-settings'
import { fillLinkTemplate, linkTemplateError, productLinkValues } from '@/pages/conversations/product-picker-lib'

function fmt(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' })
}

const OAUTH_ERRORS: Record<string, string> = {
  get denied() { return i18n.t('chanEvotech:connect.oauthErrors.denied') },
  get state_expired() { return i18n.t('chanEvotech:connect.oauthErrors.state_expired') },
  get state_invalid() { return i18n.t('chanEvotech:connect.oauthErrors.state_invalid') },
  get state_mismatch() { return i18n.t('chanEvotech:connect.oauthErrors.state_mismatch') },
  get exchange_failed() { return i18n.t('chanEvotech:connect.oauthErrors.exchange_failed') },
  get provider_error() { return i18n.t('chanEvotech:connect.oauthErrors.provider_error') },
  get no_code() { return i18n.t('chanEvotech:connect.oauthErrors.no_code') },
}

function showOAuthResult(connected: boolean, err: string | null) {
  if (err) toast.error(OAUTH_ERRORS[err] ?? i18n.t('chanEvotech:connect.oauthFailed'))
  else if (connected) toast.success(i18n.t('chanEvotech:connect.oauthSuccess'))
}

/**
 * Kết quả uỷ quyền Evotech: bấm "Kết nối" → cả tab chuyển sang Evotech → quay về chính trang này với ?evotech_connected=1 | ?evotech_error=…
 * → hiện thông báo thành công / lỗi, dọn tham số khỏi URL và tải lại dữ liệu kết nối. (Còn nghe kết quả từ cửa sổ popup của bản cũ —
 * lib/evotech-oauth-return.ts — để tab đang mở trước khi cập nhật không bị lỡ.)
 */
function useOAuthResult() {
  const qc = useQueryClient()
  useEffect(() => {
    const r = readOAuthReturn(window.location.search)
    if (r) {
      showOAuthResult(r.connected, r.error)
      qc.invalidateQueries({ queryKey: ['evotech'] })
      const q = new URLSearchParams(window.location.search)
      q.delete('evotech_connected')
      q.delete('evotech_error')
      q.delete('evotech_popup')
      const rest = q.toString()
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''))
    }
    return listenOAuthResult((m) => {
      showOAuthResult(m.connected, m.error)
      qc.invalidateQueries({ queryKey: ['evotech'] })
    })
  }, [qc])
}

/**
 * Quyền kết nối Evotech — khớp backend (evotech-routes.ts, evotech-oauth-routes.ts), theo vai trò động như mọi kênh tích hợp:
 *  - view:    xem cấu hình + trạng thái đồng bộ (integrations.view);
 *  - connect: kết nối / kết nối lại / chọn cửa hàng (integrations.create hoặc integrations.update);
 *  - update:  đồng bộ, tạm tắt / bật lại, đăng ký lại webhook, mẫu link, uỷ quyền AI (integrations.update);
 *  - remove:  ngắt kết nối (integrations.delete).
 * Thiếu quyền nào thì ẩn nút đó (BRD §5.3.1); quyền chưa tải coi như không có.
 */
export type EvotechPerms = { loading: boolean; view: boolean; connect: boolean; update: boolean; remove: boolean }

export function useEvotechPerms(): EvotechPerms {
  const { data: perms, isLoading } = useMyPermissions()
  const has = (k: string) => !!perms?.has(k)
  return {
    loading: isLoading,
    view: has('integrations.view'),
    connect: has('integrations.create') || has('integrations.update'),
    update: has('integrations.update'),
    remove: has('integrations.delete'),
  }
}

export function EvotechConnectPanel() {
  const { t } = useTranslation('chanEvotech')
  useOAuthResult()
  const quyen = useEvotechPerms()
  // Không có integrations.view thì không gọi GET /integrations/evotech (tránh 403 rồi hiện nhầm "chưa cấu hình").
  const { data: conn, isLoading } = useEvotechConnection(quyen.view)
  if (!quyen.loading && !quyen.view) {
    return (
      <p className="rounded-xl border bg-card p-5 text-sm text-muted-foreground">
        {t('connect.noViewPermission')}
      </p>
    )
  }
  if (quyen.loading || isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border bg-card p-5 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('connect.loadingConfig')}
      </div>
    )
  }
  return (
    <div className="space-y-4">
      {conn && (conn.oauthAvailable || conn.oauthConnected) ? <EvotechAccountCard conn={conn} quyen={quyen} /> : <NotConfigured />}
      {conn?.connected && <Connected conn={conn} quyen={quyen} />}
    </div>
  )
}

/** Máy chủ eCDP chưa khai báo client OAuth với Evotech (thiếu EVOTECH_OAUTH_* / EVOTECH_API_URL). */
function NotConfigured() {
  const { t } = useTranslation('chanEvotech')
  return (
    <div className="flex gap-2 rounded-xl border border-warning/40 bg-warning/10 p-5 text-sm">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
      <p>
        <Trans t={t} i18nKey="connect.notConfigured" components={{ mono: <span className="font-mono" /> }} />
      </p>
    </div>
  )
}

function EvotechAccountCard({ conn, quyen }: { conn: EvotechConnection; quyen: EvotechPerms }) {
  const { t } = useTranslation('chanEvotech')
  const startOAuth = useStartEvotechOAuth()
  const disconnect = useDisconnectEvotechOAuth()

  const connect = () => startOAuth.mutate(undefined, { onError: (err) => toast.error(apiError(err)) })

  const onDisconnect = () => {
    if (!confirm(t('connect.accountDisconnectConfirm'))) return
    disconnect.mutate(undefined, {
      onSuccess: () => toast.success(t('connect.accountDisconnected')),
      onError: (err) => toast.error(apiError(err)),
    })
  }

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <h3 className="font-medium">{t('connect.accountTitle')}</h3>
        {conn.oauthConnected && (conn.needsReauth
          ? <Badge variant="destructive">{t('connect.needsReauth')}</Badge>
          : <Badge variant="success">{t('connect.connected')}</Badge>)}
        <div className="ml-auto flex flex-wrap gap-2">
          {conn.oauthAvailable && quyen.connect && (
            <Button size="sm" variant={conn.oauthConnected ? 'outline' : 'default'} onClick={connect} disabled={startOAuth.isPending}>
              {startOAuth.isPending ? <Loader2 className="animate-spin" /> : <PlugZap />}
              {conn.oauthConnected ? t('connect.reconnect') : t('connect.connectEvotech')}
            </Button>
          )}
          {conn.oauthConnected && quyen.remove && (
            <Button size="sm" variant="outline" className="text-destructive" onClick={onDisconnect} disabled={disconnect.isPending}>
              <Unplug /> {t('connect.disconnectShort')}
            </Button>
          )}
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {conn.oauthConnected
          ? (conn.shopName
            ? t('connect.connectedWithShop', { time: fmt(conn.grantedAt), name: conn.shopName })
            : t('connect.connectedNoShop', { time: fmt(conn.grantedAt) }))
          : t('connect.redirectHint')}
      </p>
      {!conn.oauthConnected && conn.lastError && (
        <div className="mt-3 flex gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <p>{conn.lastError}</p>
        </div>
      )}
      {/* Danh sách cửa hàng (GET …/oauth/shops) và chọn cửa hàng đều cần quyền kết nối → không có thì chỉ thấy tên cửa hàng ở trên. */}
      {conn.oauthConnected && !conn.needsReauth && quyen.connect && <ShopPicker conn={conn} />}
      {conn.oauthConnected && conn.needsReauth && (
        <div className="mt-3 flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p className="text-destructive">
            {quyen.connect
              ? <Trans t={t} i18nKey="connect.reauthWithConnect" components={{ b: <b /> }} />
              : t('connect.reauthNoConnect')}
          </p>
        </div>
      )}
    </div>
  )
}

/** Chọn cửa hàng (trong tài khoản Evotech) mà eCDP được dùng. Tài khoản chỉ có một cửa hàng thì backend đã chọn sẵn. */
function ShopPicker({ conn }: { conn: EvotechConnection }) {
  const { t } = useTranslation('chanEvotech')
  const { data, isLoading, error } = useEvotechShops(true)
  const select = useSelectEvotechShop()
  const selected = conn.shopId ?? data?.selectedShopId ?? null

  if (isLoading) {
    return <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('connect.loadingShops')}</p>
  }
  if (error) return <p className="mt-3 text-xs text-destructive">{apiError(error)}</p>
  const shops = data?.shops ?? []
  if (!shops.length) return <p className="mt-3 text-xs text-muted-foreground">{t('connect.noShops')}</p>

  return (
    <div className="mt-3 space-y-2">
      <Label className="text-sm">{t('connect.shopLabel')}</Label>
      {!selected && (
        <p className="text-xs text-amber-600">{t('connect.shopPickHint')}</p>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        {shops.map((s) => {
          const on = s.shopId === selected
          return (
            <button
              key={s.shopId}
              type="button"
              disabled={select.isPending}
              onClick={() => !on && select.mutate(s.shopId, {
                onSuccess: () => toast.success(t('connect.shopSelected', { name: s.name })),
                onError: (err) => toast.error(apiError(err)),
              })}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                on ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
              )}
            >
              {on ? <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" /> : <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <span className="min-w-0">
                <span className="block truncate font-medium">{s.name}</span>
                {s.domain && <span className="block truncate text-xs text-muted-foreground">{s.domain}</span>}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function Connected({ conn, quyen }: { conn: EvotechConnection; quyen: EvotechPerms }) {
  const { t } = useTranslation('chanEvotech')
  const { data: st } = useEvotechStatus()
  const sync = useSyncEvotech()
  const disconnect = useDisconnectEvotech()
  const save = useSaveEvotech()

  const onSync = () =>
    sync.mutate(undefined, {
      onSuccess: () => toast.success(t('connect.syncStarted')),
      onError: (e) => toast.error(apiError(e)),
    })

  const onDisconnect = () => {
    if (!confirm(t('connect.disconnectConfirm'))) return
    disconnect.mutate(undefined, {
      onSuccess: (r) => toast.success(r.archivedProducts ? t('connect.disconnectedArchived', { count: r.archivedProducts }) : t('connect.disconnected')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-4">
      {/* ── Trạng thái ─────────────────────────────────────────── */}
      <div className="rounded-xl border bg-card p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Database className="h-4 w-4 text-primary" />
          <h3 className="font-medium">{t('connect.syncTitle')}</h3>
          {st?.syncing ? (
            <Badge className="gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> {t('connect.syncing')}
            </Badge>
          ) : conn.enabled ? (
            <Badge variant="success">{t('connect.active')}</Badge>
          ) : (
            <Badge variant="secondary">{t('connect.paused')}</Badge>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            {quyen.update && (
              <>
                <Button variant="outline" size="sm" onClick={onSync} disabled={sync.isPending || st?.syncing || !conn.enabled}>
                  <RefreshCw className={cn(sync.isPending && 'animate-spin')} /> {t('connect.syncNow')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    save.mutate(
                      { enabled: !conn.enabled },
                      { onError: (e) => toast.error(apiError(e)) },
                    )
                  }
                  disabled={save.isPending}
                >
                  {conn.enabled ? t('connect.paused') : t('connect.resume')}
                </Button>
              </>
            )}
            {quyen.remove && (
              <Button variant="outline" size="sm" className="text-destructive" onClick={onDisconnect} disabled={disconnect.isPending}>
                <Unplug /> {t('connect.disconnect')}
              </Button>
            )}
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          <Store className="mr-1 inline h-3.5 w-3.5" />
          <span className="font-medium">{conn.shopName ?? conn.shopId}</span>
          {conn.shopDomain ? <span className="font-mono"> · {conn.shopDomain}</span> : null}
        </p>
        <AiAuthorizationRow conn={conn} canUpdate={quyen.update} />

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <StatCard icon={Package} label={t('connect.statProducts')} value={st?.products.total ?? 0}
            sub={st ? t('connect.productsSub', { active: st.products.byStatus.active ?? 0, draft: st.products.byStatus.draft ?? 0, archived: st.products.byStatus.archived ?? 0 }) : undefined} />
          <StatCard icon={ShoppingBag} label={t('connect.statOrders')} value={st?.orders ?? 0}
            sub={st?.ordersWithoutContact ? t('connect.ordersNoContact', { count: st.ordersWithoutContact }) : t('connect.ordersHint')} />
          <StatCard icon={Users} label={t('connect.statCustomers')} value={st?.linkedContacts ?? 0}
            sub={t('connect.customersSub', { count: st?.createdFromEvotech ?? 0 })} />
        </div>

        <dl className="mt-4 grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
          <Row label={t('connect.rowLastSync')} value={fmt(st?.lastSyncAt)} />
          <Row label={t('connect.rowLastFullSync')} value={fmt(st?.lastFullSyncAt)} />
          <Row label={t('connect.rowLastCustomerSync')} value={st?.customersSyncedFrom ? fmt(st.customersSyncedFrom) : t('connect.notSynced')} />
          <Row label={t('connect.rowLastWebhook')} value={st?.lastWebhookAt ? t('connect.webhookValue', { time: fmt(st.lastWebhookAt), event: st.lastWebhookEvent ?? '' }) : t('connect.notReceived')} />
          <Row label={t('connect.rowAutoSync')} value={t('connect.every15')} />
        </dl>

        {st?.lastError && (
          <div className="mt-4 flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div>
              <p className="text-destructive">{st.lastError}</p>
              <p className="text-xs text-muted-foreground">{fmt(st.lastErrorAt)}</p>
            </div>
          </div>
        )}
      </div>

      <LinkTemplatesCard conn={conn} canUpdate={quyen.update} />

      {/* ── Webhook (eCDP tự đăng ký ở Evotech) ───────────────── */}
      <div className="rounded-xl border bg-card p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Webhook className="h-4 w-4 text-primary" />
          <h3 className="font-medium">{t('connect.webhookTitle')}</h3>
          {conn.webhookRegistered ? <Badge variant="success">{t('connect.webhookRegistered')}</Badge> : <Badge variant="secondary">{t('connect.webhookNotRegistered')}</Badge>}
          {quyen.update && (
            <Button
              variant="outline" size="sm" className="ml-auto" disabled={save.isPending || !conn.enabled}
              onClick={() => save.mutate({ reregisterWebhook: true }, {
                onSuccess: () => toast.success(t('connect.webhookReregistered')),
                onError: (e) => toast.error(apiError(e)),
              })}
            >
              <RefreshCw className={cn(save.isPending && 'animate-spin')} /> {t('connect.reregister')}
            </Button>
          )}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('connect.webhookDesc')}
        </p>
        {(conn.webhookError || conn.webhookUrlPublic === false) && (
          <div className="mt-3 flex gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p>
              {conn.webhookError ??
                t('connect.webhookNotPublic')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function StatCard({ icon: Icon, label, value, sub }: { icon: typeof Package; label: string; value: number; sub?: string }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{new Intl.NumberFormat(currentLocale()).format(value)}</p>
      {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-dashed py-1">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  )
}

/** Uỷ quyền cho nhân viên AI đọc dữ liệu Evotech (sản phẩm, tồn kho, đơn, điểm thành viên — chỉ đọc) + thu hồi. */
function AiAuthorizationRow({ conn, canUpdate }: { conn: EvotechConnection; canUpdate: boolean }) {
  const { t } = useTranslation('chanEvotech')
  const auth = useAuthorizeEvotechAi()
  const on = conn.aiAuthorized === true
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-sm">
      <span className="min-w-0 flex-1">
        <span className="font-medium">{t('connect.aiTitle')}</span>
        <span className="block text-xs text-muted-foreground">
          {on
            ? (conn.aiAuthorizedAt
              ? t('connect.aiOnSince', { date: new Date(conn.aiAuthorizedAt).toLocaleDateString(currentLocale()) })
              : t('connect.aiOn'))
            : t('connect.aiOff')}
        </span>
      </span>
      {canUpdate && (
        <Button
          variant="outline" size="sm" disabled={auth.isPending}
          onClick={() => {
            if (on && !confirm(t('connect.aiRevokeConfirm'))) return
            auth.mutate(!on, { onError: (e) => toast.error(apiError(e)), onSuccess: () => toast.success(on ? t('connect.aiRevoked') : t('connect.aiAuthorized')) })
          }}
        >
          {on ? t('connect.aiRevokeBtn') : t('connect.aiAuthorizeBtn')}
        </Button>
      )}
    </div>
  )
}

/**
 * Link sản phẩm gửi khách (nút "Sản phẩm" ở khung chat, dòng hàng trong đơn). TỰ ĐỘNG theo tên miền webapp + Zalo Mini App
 * của shop trên Evotech (giống nút "Sao chép link" ở admin Evotech). Mẫu tay chỉ còn là tuỳ chỉnh — có thì ghi đè.
 */
function LinkTemplatesCard({ conn, canUpdate }: { conn: EvotechConnection; canUpdate: boolean }) {
  const { t } = useTranslation('chanEvotech')
  const save = useSaveEvotechLinkTemplates()
  const refresh = useSaveEvotech()
  const [webapp, setWebapp] = useState(conn.webappUrlTemplate ?? '')
  const [miniapp, setMiniapp] = useState(conn.miniAppUrlTemplate ?? '')
  const hasCustom = !!(conn.webappUrlTemplate || conn.miniAppUrlTemplate)
  const [customOpen, setCustomOpen] = useState(hasCustom)
  useEffect(() => setWebapp(conn.webappUrlTemplate ?? ''), [conn.webappUrlTemplate])
  useEffect(() => setMiniapp(conn.miniAppUrlTemplate ?? ''), [conn.miniAppUrlTemplate])
  // Một sản phẩm thật đã đồng bộ để xem trước link.
  const { data: sample } = useProducts({ externalSource: 'evotech', status: 'active', pageSize: 1 })
  const example = sample?.items[0]
  const values = example ? productLinkValues(example) : null

  const errW = linkTemplateError(webapp)
  const errM = linkTemplateError(miniapp)
  const dirty = webapp.trim() !== (conn.webappUrlTemplate ?? '') || miniapp.trim() !== (conn.miniAppUrlTemplate ?? '')

  const onSave = () =>
    save.mutate(
      { webappUrlTemplate: webapp.trim(), miniAppUrlTemplate: miniapp.trim() },
      { onSuccess: () => toast.success(t('connect.tplSaved')), onError: (e) => toast.error(apiError(e)) },
    )
  const onRefresh = () =>
    refresh.mutate(
      { refreshLinks: true },
      { onSuccess: () => toast.success(t('connect.linksRefreshed')), onError: (e) => toast.error(apiError(e)) },
    )

  const rows = [
    {
      key: 'webapp', icon: Globe, label: 'Webapp', custom: conn.webappUrlTemplate, auto: conn.autoLinks?.webapp ?? null,
      source: conn.shopWebDomain ?? conn.shopDomain ?? null, missing: t('connect.missingWebapp'),
    },
    {
      key: 'miniapp', icon: Smartphone, label: 'Zalo Mini App', custom: conn.miniAppUrlTemplate, auto: conn.autoLinks?.miniapp ?? null,
      source: conn.shopMiniAppId ? `Mini App ${conn.shopMiniAppId}` : null, missing: t('connect.missingMiniapp'),
    },
  ] as const

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Link2 className="h-4 w-4 text-primary" />
        <h3 className="font-medium">{t('connect.linksTitle')}</h3>
        <Badge variant="success" className="text-[10px]">{t('connect.auto')}</Badge>
        {canUpdate && (
          <Button size="sm" variant="outline" className="ml-auto h-8" onClick={onRefresh} disabled={refresh.isPending}>
            {refresh.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />} {t('connect.refreshLinks')}
          </Button>
        )}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        <Trans t={t} i18nKey="connect.linksDesc" components={{ b: <b /> }} />
      </p>

      <div className="mt-4 divide-y rounded-lg border">
        {rows.map((r) => {
          const tpl = r.custom || r.auto
          const link = values ? fillLinkTemplate(tpl, values) : null
          return (
            <div key={r.key} className="flex flex-col gap-1 p-3 sm:flex-row sm:items-start sm:gap-3">
              <div className="flex w-40 shrink-0 items-center gap-1.5 text-sm font-medium">
                <r.icon className="h-4 w-4 text-muted-foreground" /> {r.label}
              </div>
              <div className="min-w-0 flex-1 space-y-0.5 text-xs">
                {tpl ? (
                  <>
                    <p className="text-muted-foreground">
                      {r.custom
                        ? <Trans t={t} i18nKey="connect.usingCustom" components={{ b: <b className="text-foreground" /> }} />
                        : <Trans t={t} i18nKey="connect.usingSource" values={{ source: r.source }} components={{ b: <b className="text-foreground" /> }} />}
                    </p>
                    {link ? (
                      <a href={link} target="_blank" rel="noreferrer" className="block break-all font-mono text-primary hover:underline">{link}</a>
                    ) : (
                      <p className="break-all font-mono text-muted-foreground">{tpl}</p>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground">{t('connect.missingDimmed', { reason: r.missing })}</p>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {conn.shopLinksSyncedAt && (
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          {t('connect.syncedAt', { time: new Date(conn.shopLinksSyncedAt).toLocaleString(currentLocale()) })}
          {example ? <Trans t={t} i18nKey="connect.exampleSuffix" values={{ name: example.name }} components={{ b: <b className="font-medium" /> }} /> : null}
        </p>
      )}

      {/* Sửa mẫu link (PUT …/link-templates) cần integrations.update → không có thì ẩn cả phần tuỳ chỉnh. */}
      {canUpdate && (
        <button
          type="button"
          onClick={() => setCustomOpen((v) => !v)}
          className="mt-4 flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          aria-expanded={customOpen}
        >
          {customOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {t('connect.customToggle')}
        </button>
      )}
      {canUpdate && customOpen && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-muted-foreground">{t('connect.customHint')}</p>
          <div className="flex flex-wrap gap-1.5 text-[11px]">
            {[
              ['{itemsCode}', t('connect.varItemsCode')],
              ['{parentNo}', t('connect.varParentNo')],
              ['{itemNo}', t('connect.varItemNo')],
              ['{code}', t('connect.varCode')],
            ].map(([k, v]) => (
              <span key={k} className="rounded border bg-muted/50 px-1.5 py-0.5">
                <span className="font-mono">{k}</span> — {v}
              </span>
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TemplateField
              id="evo-webapp-tpl"
              icon={Globe}
              label={t('connect.tplWebapp')}
              value={webapp}
              onChange={setWebapp}
              placeholder={conn.autoLinks?.webapp ?? t('connect.phWebapp')}
              error={errW}
              example={example ? fillLinkTemplate(webapp.trim(), productLinkValues(example)) : null}
              exampleName={example?.name}
            />
            <TemplateField
              id="evo-miniapp-tpl"
              icon={Smartphone}
              label={t('connect.tplMiniapp')}
              value={miniapp}
              onChange={setMiniapp}
              placeholder={conn.autoLinks?.miniapp ?? t('connect.phMiniapp')}
              error={errM}
              example={example ? fillLinkTemplate(miniapp.trim(), productLinkValues(example)) : null}
              exampleName={example?.name}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <p className="mr-auto text-[11px] text-muted-foreground">{t('connect.tplRules')}</p>
            <Button size="sm" onClick={onSave} disabled={!dirty || !!errW || !!errM || save.isPending}>
              {save.isPending && <Loader2 className="animate-spin" />} {t('connect.tplSave')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function TemplateField({
  id, icon: Icon, label, value, onChange, placeholder, error, example, exampleName,
}: {
  id: string
  icon: typeof Globe
  label: string
  value: string
  onChange: (v: string) => void
  placeholder: string
  error: string | null
  example: string | null
  exampleName?: string
}) {
  const { t } = useTranslation('chanEvotech')
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" /> {label}
      </Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        autoComplete="off"
        className={cn('font-mono text-xs', error && 'border-destructive focus-visible:ring-destructive/40')}
      />
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : !value.trim() ? (
        <p className="text-xs text-muted-foreground">{t('connect.fieldEmpty')}</p>
      ) : example ? (
        <p className="text-xs text-muted-foreground">
          {exampleName
            ? <Trans t={t} i18nKey="connect.exampleWithName" values={{ name: exampleName }} components={{ b: <b className="font-medium text-foreground" /> }} />
            : t('connect.exampleNoName')}{' '}
          <a href={example} target="_blank" rel="noreferrer" className="break-all font-mono text-primary hover:underline">{example}</a>
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {exampleName ? t('connect.sampleMissingData', { name: exampleName }) : t('connect.noSample')}
        </p>
      )}
    </div>
  )
}
